import React, { useContext } from "react";
import {
  Button,
  Loading,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectRow,
  TableToolbar,
  TableToolbarContent,
} from "carbon-components-react";

import DollarInput from "web_modules/shared/carbon/DollarInput";

import { Role } from "@eave/shared/types/api";
import { getSessionToken, tokenHasRoles } from "web_modules/shared/session";
import HeaderInfo from "./HeaderInfo";
import { PointsAndCreditsContext } from "../PointsAndCreditsProvider";
import { dollarsTo2Places, percentTo2Places, percentTo3Places } from "./helpers";

const styles = require("./styles.module.scss");

const pointsOrCredits = (pc: number): string => {
  if (pc === 0) {
    return "—";
  }
  return pc < 0 ? "Points paid at closing" : "Credit to borrower";
};

// TODO: jjw: put field keys in shared/types/api/rate-stack.ts for consts here
const headers = [
  "Note rate",
  "Price delta",
  "Amount",
  "Points/credits",
  "Monthly P&I payment",
  "DTI",
];

const PointsAndCreditsScreen = () => {
  const {
    busy,
    rateStack: { loan_amount, locked, rate_stack },
    selectedPriceDelta,
    setSelectedPriceDelta,
    saveSelectedPriceDelta,
  } = useContext(PointsAndCreditsContext);

  const hasCorrectRole = tokenHasRoles(getSessionToken(), Role.LoanOfficer);
  const disabled = busy || !locked || !hasCorrectRole;

  const selectedRate = rate_stack.find(r => r.selected);
  const originalPriceDelta = selectedRate ? selectedRate.price_delta : null;

  const sortedRateStack = rate_stack.sort((r1, r2) => {
    if (r1.price_delta > r2.price_delta) {
      return -1;
    }
    if (r1.price_delta < r2.price_delta) {
      return 1;
    }
    return 0;
  });

  return (
    <div className={styles.container}>
      <h1>Points & credits</h1>
      <HeaderInfo />
      <div className={styles.amount}>
        <DollarInput
          id="loan-amount"
          name="loan_amount"
          value={loan_amount}
          labelText="Loan amount"
          disabled
        />
      </div>
      <TableContainer useStaticWidth>
        <TableToolbar>
          <TableToolbarContent>
            {busy && (
              <div className={styles.busy}>
                <Loading small active withOverlay={false} />
              </div>
            )}
            <Button
              disabled={disabled || selectedPriceDelta === originalPriceDelta}
              onClick={saveSelectedPriceDelta}
            >
              {busy ? "Saving…" : "Select this rate"}
            </Button>
          </TableToolbarContent>
        </TableToolbar>
        <Table size="sm" useZebraStyles>
          <TableHead>
            <TableRow>
              <th scope="col" />
              {headers.map(h => (
                <TableHeader key={h} className={h === "Points/credits" ? styles.left : undefined}>
                  {h}
                </TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRateStack.map(r => {
              const {
                error, // ineligible
                displayed_loan_rate,
                price_delta,
                points_or_credits,
                monthly_payment,
                dti,
              } = r;

              return (
                <TableRow
                  key={`row-${price_delta}`}
                  className={!locked || error ? styles.disabled : undefined}
                >
                  <TableSelectRow
                    radio
                    id={`select-${price_delta}`}
                    name={`select_${price_delta}`}
                    ariaLabel={`Select ${price_delta}`}
                    disabled={disabled || error}
                    checked={selectedPriceDelta === price_delta}
                    onSelect={() => setSelectedPriceDelta(price_delta)}
                  />
                  <TableCell className={styles.bold}>
                    {percentTo3Places(displayed_loan_rate)}
                  </TableCell>
                  <TableCell>{percentTo3Places(price_delta)}</TableCell>
                  <TableCell>{dollarsTo2Places(Math.abs(points_or_credits))}</TableCell>
                  <TableCell className={styles.left}>
                    {pointsOrCredits(points_or_credits)}
                  </TableCell>
                  <TableCell>{dollarsTo2Places(monthly_payment)}</TableCell>
                  <TableCell>{percentTo2Places(100 * dti)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default PointsAndCreditsScreen;
